<template>
  <PersonalNotesModal
    v-if="showPersonalNotesModal && personalNotesModalWorkflow"
    :workflowId="personalNotesModalWorkflow.id"
    :value="personalNotesModalWorkflow.LatestUserComment"
    @closeModal="closePersonalNotesModal"
  />
  <div class="workflow-table" data-cy="workflow-table">
    <div
      class="grid-control__container"
      data-cy="workflow-table-control-container"
    >
      <div class="grid-control__container-left">
        <div
          v-if="dataRangeVisible"
          class="grid-control__container-date-wrapper"
        >
          <DateRangePicker
            class="grid-control__container-date-picker"
            :value="dateRange"
            @change="handleChange"
            data-cy="date-range-picker"
          />
          <Button
            :theme-color="'primary'"
            type="button"
            :disabled="workflowsLoading"
            @click="filterByDate"
            data-cy="search-date-button"
            >By Date</Button
          >
          <span class="grid-control__container-text"> OR </span>
        </div>
        <input
          type="text"
          v-model="jobId"
          placeholder="Search by Job ID"
          @keydown.enter.prevent="filterByJobId"
          data-cy="job-id-input"
        />
        <Button
          :theme-color="'primary'"
          type="button"
          :disabled="workflowsLoading"
          @click="filterByJobId"
          data-cy="search-job-id-button"
          >By Job ID</Button
        >
      </div>
      <div class="grid-control__container-right pull-right">
        <ClearFiltersButton @clearFiltersClicked="clearFilters" />
      </div>
    </div>
    <WorkflowGrid
      :workflows="workflows"
      :filter="filter"
      :loading="workflowsLoading"
      :skip="skip"
      :pageSize="take"
      :total="totalWorkflows"
      :sort="sortDescriptors"
      :getPropValues="getPropValuesAsync"
      @sortChanged="onSortChanged"
      @itemsChanged="onWorkflowsChanged"
      @privateNotesClicked="onPrivateNotesClicked"
      @columnreorder="updateColumnMenuIcons"
      @columnssubmit="updateColumnMenuIcons"
    />
    <ColumnMenuIconHandler :filter="filter" ref="menuIconHandlerRef" />
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { computed, onBeforeMount, ref } from "vue";
import WorkflowGrid from "./WorkflowGrid.vue";
import { Button } from "@progress/kendo-vue-buttons";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import ClearFiltersButton from "@/shared/ui/inputs/ClearFiltersButton.vue";
import {
  FilterValuesTake,
  getJsonFilterString,
  valueFilterConvert,
} from "@/shared/helpers/filtersHelper";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import type { SelectionRange } from "@progress/kendo-vue-dateinputs";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";

import type { GetWorkflowsWithFilteringRequest } from "@/api/workflowOverview";
import { EnumWorkflowMode } from "@/Domain/EnumWorkflowMode";
import type { WfSubmissionProductAndIngredient } from "@/Domain/WfSubmissionProductAndIngredient";
import PersonalNotesModal from "./PersonalNotesModal.vue";
import { EnumSubmissionType } from "@/Domain/EnumSubmissionType";
import enumHelper from "@/shared/helpers/enumHelper";
import { EnumWorkflowStatus } from "@/Domain/EnumWorkflowStatus";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import DateRangePicker from "@/shared/ui/inputs/DateRangePicker.vue";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const props = defineProps<{
  mode: EnumWorkflowMode;
}>();

enum SearchMode {
  ByDate = "SearchByDateRange",
  ByJobId = "SearchByJobId",
}

const urlParams = new URLSearchParams(window.location.search);
const currentPageIndex =
  parseInt(urlParams.get("CurrentPageIndex") || "0") || 0;

const workflows = ref<WfSubmissionProductAndIngredient[]>([]);
const totalWorkflows = ref<number>(0);
const workflowsLoading = ref<boolean>(false);
const skip = ref<number>(100 * currentPageIndex);
const take = ref<number>(100);

const sortField = ref<string>("");
const sortAscending = ref<boolean>(false);
const jobId = ref<string>("");
const showPersonalNotesModal = ref<boolean>(false);
const personalNotesModalWorkflow = ref<WfSubmissionProductAndIngredient | null>(
  null
);
const selectedSearchMode = ref<SearchMode>(SearchMode.ByDate);

const tableName = "WorkflowOverview_" + props.mode.toString();
const menuIconHandlerRef = ref<InstanceType<
  typeof ColumnMenuIconHandler
> | null>(null);

const filter = ref<CompositeFilterDescriptor>(
  GridOptionsService.loadGridFilter(tableName)
);

const sortDescriptors = computed(() => {
  return (
    sortField.value
      ? [
          {
            field: sortField.value,
            dir: sortAscending.value ? "asc" : "desc",
          },
        ]
      : []
  ) as SortDescriptor[];
});

const valueFiltersConvertOptions = [
  {
    column: "JobType",
    convert: (value: number) => {
      return enumHelper.splitByCapitalLetter(EnumSubmissionType[value]);
    },
    convertBack: (value: string) => {
      const key = enumHelper.joinByCapitalLetter(EnumSubmissionType, value);
      return EnumSubmissionType[key as keyof typeof EnumSubmissionType];
    },
  },
  {
    column: "SubmissionDate",
    convert: (value: string) => {
      return new Date(value).toLocaleString("en-US");
    },
    convertBack: (value: string) => {
      return value;
    },
  },
  {
    column: "LastModified",
    convert: (value: string) => {
      return new Date(value).toLocaleString("en-US");
    },
    convertBack: (value: string) => {
      return value;
    },
  },
  {
    column: "Status",
    convert: (value: number) => {
      return enumHelper.splitByCapitalLetter(EnumWorkflowStatus[value]);
    },
    convertBack: (value: string) => {
      const key = enumHelper.joinByCapitalLetter(EnumWorkflowStatus, value);
      return EnumWorkflowStatus[key as keyof typeof EnumWorkflowStatus];
    },
  },
] as ValueFieldConvertOption[];

const currentDate = new Date();
const startDate = new Date(
  currentDate.getFullYear(),
  currentDate.getMonth() - 1,
  currentDate.getDate()
);

const myWorkDefaultStartDate = new Date(
  currentDate.getFullYear(),
  currentDate.getMonth() - 5,
  currentDate.getDate()
);

const dateRange = ref({
  start: startDate,
  end: currentDate,
} as SelectionRange);

// Hide the date picker for 'my work'.
// Show all work assigned to the person if it's open or in progress
const dataRangeVisible = computed(() => {
  return props.mode != EnumWorkflowMode.My;
});

const handleChange = function (newRange: SelectionRange) {
  dateRange.value = newRange;
};

const getSubmissionsWithFilteringAsync = async function (
  valuesForFieldOnly: string = ""
) {
  const requestStartDate =
    props.mode == EnumWorkflowMode.My
      ? myWorkDefaultStartDate
      : dateRange.value.start;

  const requestParams = {
    beginDate:
      selectedSearchMode.value != SearchMode.ByDate
        ? ""
        : requestStartDate?.toLocaleDateString("en-US"),
    endDate:
      selectedSearchMode.value != SearchMode.ByDate
        ? ""
        : dateRange.value.end?.toLocaleDateString("en-US"),
    mode: props.mode.toString(),
    searchMode: selectedSearchMode.value,
    jobId: selectedSearchMode.value == SearchMode.ByJobId ? jobId.value : "",
    filters: getJsonFilterString(filter.value, valueFiltersConvertOptions),
    skip: skip.value,
    take: take.value,
    valuesForFieldOnly: valuesForFieldOnly,
    sortField: sortField.value,
    ascending: sortAscending.value,
  } as GetWorkflowsWithFilteringRequest;

  if (!valuesForFieldOnly)
    return api.get("GetWorkflowsWithFiltering", requestParams);

  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getAsync(createCacheKey(requestParams), () =>
    api.get("GetWorkflowsWithFiltering", requestParams)
  );
};

const getWorkflowsAsync = async function (resetTimer: boolean = true) {
  if (workflowsLoading.value) return;
  workflowsLoading.value = true;

  // if (resetTimer) {
  //   setRefreshTimer();
  // }

  await handleResponsePromiseAsync(
    getSubmissionsWithFilteringAsync(),
    (data) => {
      workflows.value = data.ListItems;
      totalWorkflows.value = data.TotalItemsInList;
    },
    "WorkflowTable.vue",
    "getWorkflowsAsync"
  );

  workflowsLoading.value = false;
};

const getPropValuesAsync = async function (column: GridColumnPropsExtended) {
  let result: WfSubmissionProductAndIngredient[] = [];
  await handleResponsePromiseAsync(
    getSubmissionsWithFilteringAsync(column.field),
    (data) => {
      result = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, column.field!, result);
    },
    "WorkflowTable.vue",
    "getPropValuesAsync"
  );
  return result;
};

const onWorkflowsChanged = function (options: ItemChangedOptions) {
  if (options.filterEvent) {
    filter.value.filters = options.filterEvent.filter.filters;
    GridOptionsService.saveGridFilter(tableName, filter.value);
  }
  skip.value = options.skip;
  if (take.value != options.take) {
    take.value = options.take;
    GridOptionsService.savePageSizeAsync(tableName, options.take);
  }
  getWorkflowsAsync();
};

const onPrivateNotesClicked = function (wf: WfSubmissionProductAndIngredient) {
  personalNotesModalWorkflow.value = wf;
  showPersonalNotesModal.value = true;
};

const closePersonalNotesModal = function (updated: boolean, newNotes: string) {
  if (updated) {
    personalNotesModalWorkflow.value!.LatestUserComment = newNotes;
  }
  personalNotesModalWorkflow.value = null;
  showPersonalNotesModal.value = false;
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  skip.value = 0;
  getWorkflowsAsync();
};

const filterByDate = function () {
  selectedSearchMode.value = SearchMode.ByDate;
  getWorkflowsAsync();
};

const filterByJobId = function () {
  selectedSearchMode.value = SearchMode.ByJobId;
  getWorkflowsAsync();
};

const clearFilters = function () {
  console.debug("WorkflowTable.vue, clearFilters()");
  filter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };

  GridOptionsService.saveGridFilter(tableName, filter.value);
  sortField.value = "";
  sortAscending.value = false;
  jobId.value = "";
  selectedSearchMode.value = SearchMode.ByDate;
  dateRange.value = {
    start: startDate,
    end: currentDate,
  } as SelectionRange;
  getWorkflowsAsync();
};

const updateColumnMenuIcons = function () {
  setTimeout(() => {
    menuIconHandlerRef.value?.updateColumnMenuIcons();
  }, 100);
};

const refreshTimerId = ref<number>(0);

const setRefreshTimer = function () {
  if (refreshTimerId.value) {
    clearInterval(refreshTimerId.value);
  }

  refreshTimerId.value = window.setInterval(
    () => getWorkflowsAsync(false),
    60000
  );
};

onBeforeMount(async () => {
  const pageSize = await GridOptionsService.getPageSizeAsync(tableName);
  if (pageSize) take.value = pageSize;
  await getWorkflowsAsync();
});

// onBeforeUnmount(() => {
//   if (refreshTimerId.value) {
//     clearInterval(refreshTimerId.value);
//   }
// });
</script>

<style lang="scss">
.workflow-table {
  .grid-control__container {
    display: flex;
    align-items: flex-end;

    &-left,
    &-date-wrapper {
      display: flex;
      gap: 16px;
      align-items: flex-end;
    }

    &-date-picker {
      input {
        border: unset;
      }
    }

    &-text {
      height: 32px;
      display: inline-flex;
      align-items: center;
    }
  }

  .pull-right {
    display: flex;
    margin-left: auto;
    gap: 8px;
  }

  .form-link {
    text-decoration: none;
  }
}
</style>
